import React, { useState } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Select, DatePicker, Input, message, Typography, Switch, Popconfirm, Space, Drawer, Descriptions, Empty, Tooltip, List, Grid } from 'antd';
import { PlusOutlined, CheckOutlined, UndoOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmApi } from '../../../api/client';
import dayjs from 'dayjs';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const outcomeMap: Record<string, string> = {
    NO_RESPONSE: 'Brak odpowiedzi',
    CONTACTED: 'Skontaktowano',
    INTERESTED: 'Zainteresowany',
    NOT_INTERESTED: 'Brak zainteresowania',
    OFFER_SENT: 'Oferta wysłana',
    WAITING_FOR_REPLY: 'Oczekiwanie na odp.',
    CALLBACK_LATER: 'Oddzwonić później',
    SAMPLES_REQUESTED: 'Zamówiono próbki',
    SAMPLES_SENT: 'Próbki wysłane',
    MEETING_PLANNED: 'Spotkanie zaplanowane',
    WON: 'Wygrana',
    LOST: 'Przegrana',
    OTHER: 'Inne',
};

const directionMap: Record<string, string> = {
    INBOUND: 'Przychodzący',
    OUTBOUND: 'Wychodzący',
    NONE: 'N/A',
};

const typeConfig: Record<string, { label: string, color: string }> = {
    EMAIL: { label: 'E-mail', color: 'blue' },
    PHONE: { label: 'Telefon', color: 'green' },
    MEETING: { label: 'Spotkanie', color: 'purple' },
    MESSAGE: { label: 'Wiadomość', color: 'orange' },
    OFFER: { label: 'Oferta', color: 'cyan' },
    SAMPLES: { label: 'Próbki', color: 'magenta' },
    FOLLOWUP: { label: 'Follow-up', color: 'volcano' },
    NOTE: { label: 'Notatka', color: 'default' },
    OTHER: { label: 'Inne', color: 'default' },
};

const directionOptions = [
    { value: 'INBOUND', label: 'Przychodzący' },
    { value: 'OUTBOUND', label: 'Wychodzący' },
    { value: 'NONE', label: 'Nie dotyczy' },
];

const outcomeOptions = Object.entries(outcomeMap).map(([value, label]) => ({ value, label }));

export const ContractorInteractions = ({ contractorId, contacts, onUpdate }: { contractorId: string, contacts: any[], onUpdate: () => void }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState<any>(null);
    const [editingInteraction, setEditingInteraction] = useState<any>(null);
    const [form] = Form.useForm();
    const isFollowUpRequired = Form.useWatch('isFollowUpRequired', form);

    const { data: response, isLoading } = useQuery({
        queryKey: ['interactions', contractorId],
        queryFn: () => crmApi.getContractorInteractions(contractorId),
    });

    const interactions = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['interactions', contractorId] });
        queryClient.invalidateQueries({ queryKey: ['contractor', contractorId] });
    };

    const addMutation = useMutation({
        mutationFn: (data: any) => crmApi.addContractorInteraction(data),
        onSuccess: () => {
            invalidateAll();
            onUpdate();
            setIsModalOpen(false);
            form.resetFields();
            message.success('Wpis został dodany');
        },
        onError: (err: any) => message.error('Błąd zapisu: ' + (err.response?.data?.message || 'Nieznany błąd'))
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => crmApi.updateContractorInteraction(editingInteraction?.id || data.id, data),
        onSuccess: () => {
            invalidateAll();
            onUpdate();
            setEditingInteraction(null);
            setIsModalOpen(false);
            form.resetFields();
            message.success('Zapisano zmiany');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => crmApi.deleteContractorInteraction(id),
        onSuccess: () => {
            invalidateAll();
            onUpdate();
            message.success('Wpis usunięty');
        }
    });

    const handleSubmit = (values: any) => {
        const payload = { ...values, contactId: Array.isArray(values.contactId) ? values.contactId[0] : values.contactId, date: values.date.toISOString(), nextStepDate: values.nextStepDate?.toISOString(), contractorId };
        if (editingInteraction) {
            updateMutation.mutate(payload);
        } else {
            addMutation.mutate(payload);
        }
    };

    const handleOpenDetail = (r: any) => {
        setDetailData(r);
        setDetailModalOpen(true);
    };

    const columns = [
        { title: 'Data', dataIndex: 'date', width: 150, render: (d: string, r: any) => <a onClick={() => handleOpenDetail(r)}>{dayjs(d).format('YYYY-MM-DD HH:mm')}</a> },
        { 
            title: 'Typ / Kierunek', 
            align: 'center',
            render: (_: any, r: any) => (
                <Space size={4} direction="vertical">
                    <Tag color={typeConfig[r.type]?.color}>{typeConfig[r.type]?.label}</Tag>
                    {directionMap[r.direction] && <Text type="secondary" style={{ fontSize: '11px' }}>{directionMap[r.direction]}</Text>}
                </Space>
            )
        },
        { title: 'Osoba', dataIndex: ['contact', 'lastName'], render: (_: any, r: any) => r.contact ? `${r.contact.firstName} ${r.contact.lastName}` : '-' },
        { title: 'Wynik', dataIndex: 'outcome', render: (o: string) => <Tag>{outcomeMap[o] || o}</Tag> },
        { 
            title: 'Dalszy krok', 
            dataIndex: 'nextStep', 
            render: (val: string, r: any) => r.isFollowUpRequired ? (
                <Tooltip title={`Termin: ${dayjs(r.nextStepDate).format('YYYY-MM-DD')}`}>
                    <Text type={dayjs(r.nextStepDate).isBefore(dayjs()) ? 'danger' : 'warning'}>
                        {val}
                    </Text>
                </Tooltip>
            ) : <Tag color="green">Wykonano</Tag>
        },
        { title: 'Autor', dataIndex: ['createdBy', 'lastName'], render: (_: any, r: any) => r.createdBy?.lastName || 'System' },
        { title: 'Akcje', width: 140, render: (_: any, r: any) => (
            <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(r)} />
                <Button size="small" icon={<EditOutlined />} onClick={() => { 
                    setEditingInteraction(r); 
                    form.setFieldsValue({ ...r, date: dayjs(r.date), nextStepDate: r.nextStepDate ? dayjs(r.nextStepDate) : null }); 
                    setIsModalOpen(true); 
                }} />
                {r.isFollowUpRequired ? (
                    <Tooltip title="Oznacz jako wykonane"><Button size="small" icon={<CheckOutlined />} onClick={() => updateMutation.mutate({ ...r, isFollowUpRequired: false })} /></Tooltip>
                ) : (
                    <Tooltip title="Cofnij - oznacz jako aktywne"><Button size="small" icon={<UndoOutlined />} onClick={() => updateMutation.mutate({ ...r, isFollowUpRequired: true })} /></Tooltip>
                )}
            </Space>
        ) }
    ];

    return (
        <Card title="Historia kontaktów" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingInteraction(null); form.resetFields(); setIsModalOpen(true); }}>Dodaj wpis</Button>}>
            {isMobile ? (
                <List
                    dataSource={interactions}
                    renderItem={(item: any) => (
                        <Card size="small" style={{ marginBottom: 12 }} onClick={() => handleOpenDetail(item)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>{item.subject}</Text>
                                <Tag color={typeConfig[item.type]?.color}>{typeConfig[item.type]?.label}</Tag>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(item.date).format('YYYY-MM-DD HH:mm')}</Text>
                        </Card>
                    )}
                />
            ) : (
                <Table 
                    dataSource={interactions} 
                    columns={columns} 
                    loading={isLoading} 
                    rowKey="id" 
                    scroll={{ x: 600 }}
                    locale={{ emptyText: <Empty description="Brak historii kontaktów" /> }}
                />
            )}
            
            <Modal title="Szczegóły wpisu" open={detailModalOpen} onCancel={() => setDetailModalOpen(false)} footer={null} width={500}>
                {detailData && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Data">{dayjs(detailData.date).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                            <Descriptions.Item label="Kierunek">{directionMap[detailData.direction] || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Typ">{typeConfig[detailData.type]?.label}</Descriptions.Item>
                            <Descriptions.Item label="Osoba kontaktowa">{detailData.contact ? `${detailData.contact.firstName} ${detailData.contact.lastName}` : '-'}</Descriptions.Item>
                            <Descriptions.Item label="Temat">{detailData.subject}</Descriptions.Item>
                            <Descriptions.Item label="Opis">{detailData.description || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Wynik">{outcomeMap[detailData.outcome] || detailData.outcome}</Descriptions.Item>
                            <Descriptions.Item label="Szczegóły wyniku">{detailData.outcomeDetails || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Wymaga działań">{detailData.isFollowUpRequired ? 'Tak' : 'Nie'}</Descriptions.Item>
                            {detailData.isFollowUpRequired && (
                                <>
                                    <Descriptions.Item label="Następny krok">{detailData.nextStep}</Descriptions.Item>
                                    <Descriptions.Item label="Termin">{dayjs(detailData.nextStepDate).format('YYYY-MM-DD')}</Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label="Autor">{detailData.createdBy?.lastName || 'System'}</Descriptions.Item>
                        </Descriptions>
                        <Space>
                            <Button type="primary" icon={<EditOutlined />} onClick={() => { setEditingInteraction(detailData); form.setFieldsValue({ ...detailData, date: dayjs(detailData.date), nextStepDate: detailData.nextStepDate ? dayjs(detailData.nextStepDate) : null }); setIsModalOpen(true); setDetailModalOpen(false); }}>Edytuj</Button>
                            <Popconfirm title="Usunąć ten wpis?" onConfirm={() => { deleteMutation.mutate(detailData.id); setDetailModalOpen(false); }}><Button danger icon={<DeleteOutlined />}>Usuń</Button></Popconfirm>
                        </Space>
                    </Space>
                )}
            </Modal>
            
            <Modal title={editingInteraction ? "Edycja wpisu" : "Nowy wpis"} open={isModalOpen} onCancel={() => setIsModalOpen(false)} onOk={() => form.submit()} width={600}>
                <Form form={form} initialValues={{ date: dayjs(), type: 'NOTE', direction: 'NONE', outcome: 'CONTACTED', isFollowUpRequired: false, description: '' }} onFinish={handleSubmit} layout="vertical">
                    <Form.Item name="date" label="Data" rules={[{ required: true }]}><DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="direction" label="Kierunek" rules={[{ required: true }]}><Select options={directionOptions} /></Form.Item>
                    <Form.Item name="type" label="Typ" rules={[{ required: true }]}><Select options={Object.entries(typeConfig).map(([k, v]) => ({ value: k, label: v.label }))} /></Form.Item>
                    <Form.Item name="contactId" label="Osoba kontaktowa">
                        <Select 
                            mode="tags"
                            tokenSeparators={[',']}
                            placeholder="Wybierz lub wpisz imię i nazwisko"
                            options={contacts.map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))} 
                        />
                    </Form.Item>
                    <Form.Item name="subject" label="Temat" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="description" label="Opis"><Input.TextArea /></Form.Item>
                    <Form.Item name="outcome" label="Wynik" rules={[{ required: true }]}><Select options={outcomeOptions} /></Form.Item>
                    <Form.Item name="outcomeDetails" label="Szczegóły wyniku"><Input.TextArea /></Form.Item>
                    <Form.Item name="isFollowUpRequired" label="Wymaga dalszego działania" valuePropName="checked"><Switch /></Form.Item>
                    {isFollowUpRequired && (
                        <>
                            <Form.Item name="nextStep" label="Następny krok" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="nextStepDate" label="Termin" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </Card>
    );
};
